import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const ProfilePicture = ({
  src,
  alt,
  className = '',
  fallbackUrl = 'https://res.cloudinary.com/dez2cotie/image/upload/v1748006632/Pics2_jfez90.webp',
  verifiedTitle,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src || fallbackUrl);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setCurrentSrc(src || fallbackUrl);
  }, [src, fallbackUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    if (currentSrc !== fallbackUrl) {
      setCurrentSrc(fallbackUrl);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gray-700 rounded-full animate-pulse flex items-center justify-center">
          <i className="fas fa-user text-gray-500 text-sm"></i>
        </div>
      )}
      <img
        src={currentSrc}
        alt={alt}
        className={`h-full w-full object-cover transition-opacity duration-200 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      {src && src !== fallbackUrl && (
        <div
          className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full"
          title={verifiedTitle}
        />
      )}
    </div>
  );
};

function TopCreators() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [topCreators, setTopCreators] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const fetchTopCreators = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const response = await fetch('/.netlify/functions/leaderboard?type=creators');
        if (!response.ok) {
          throw new Error('Failed to fetch top creators data');
        }
        const data = await response.json();
        setTopCreators(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching top creators:', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTopCreators();
  }, []);

  const getRankStyling = (index) => {
    switch (index) {
      case 0:
        return {
          bgClass: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
          textClass: 'text-black',
          icon: '👑',
        };
      case 1:
        return {
          bgClass: 'bg-gradient-to-r from-gray-300 to-gray-500',
          textClass: 'text-black',
          icon: '🥈',
        };
      case 2:
        return {
          bgClass: 'bg-gradient-to-r from-amber-600 to-amber-800',
          textClass: 'text-white',
          icon: '🥉',
        };
      default:
        return {
          bgClass: 'bg-cyan-500',
          textClass: 'text-white',
          icon: null,
        };
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-gradient-to-b from-[#121214] to-black py-16">
        <div className="container mx-auto px-4">
          <h1
            className="text-4xl md:text-5xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]"
            style={{ fontFamily: 'cursive, Arial, sans-serif' }}
          >
            🎨 {t('topCreators')}
          </h1>
          <p className="text-gray-300 text-center mt-4 max-w-2xl mx-auto">{t('discoverTopCreatorsGlobal')}</p>
          {!loading && !loadFailed && topCreators.length > 0 && (
            <p className="text-cyan-400 text-center mt-2 text-sm">
              {topCreators.length}{' '}
              {topCreators.length > 1 ? t('creatorsCountPlural') : t('creatorsCount')} • {t('updatedRealTime')}
            </p>
          )}
        </div>
      </div>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-[#121214] border border-[#232329] rounded-lg shadow-lg shadow-cyan-500/5 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-32">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
              </div>
            ) : loadFailed ? (
              <div className="text-center py-32">
                <div className="text-6xl mb-4">😔</div>
                <p className="text-red-500 mb-4">{t('topCreatorsLoadError')}</p>
                <button
                  type="button"
                  className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-md transition duration-300"
                  onClick={() => window.location.reload()}
                >
                  {t('retryButton')}
                </button>
              </div>
            ) : topCreators.length === 0 ? (
              <div className="text-center py-32">
                <div className="text-6xl mb-4">🎨</div>
                <p className="text-gray-400 mb-2">{t('noCreatorsYet')}</p>
                <p className="text-gray-500 text-sm">{t('beFirstToSubmitCreators')}</p>
              </div>
            ) : (
              <>
                <div className="bg-[#18181B] py-4 px-6">
                  <div className="grid grid-cols-12 text-gray-200 font-medium">
                    <div className="col-span-2 text-center">{t('rank')}</div>
                    <div className="col-span-5">{t('creator')}</div>
                    <div className="col-span-3 text-center">{t('designsColumn')}</div>
                    <div className="col-span-2 text-center">{t('votes')}</div>
                  </div>
                </div>
                <div className="divide-y divide-[#232329]">
                  {topCreators.map((creator, index) => {
                    const rankStyle = getRankStyling(index);
                    const uploadCount = creator.count || creator.upload_count || 0;
                    return (
                      <div
                        key={`creator-${index}`}
                        className="grid grid-cols-12 items-center py-4 px-6 hover:bg-[#1c1c20] transition-colors duration-200"
                      >
                        <div className="col-span-2 flex justify-center">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${rankStyle.bgClass} ${rankStyle.textClass} shadow-lg`}
                          >
                            {rankStyle.icon || index + 1}
                          </div>
                        </div>
                        <div className="col-span-5 flex items-center">
                          <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-cyan-500 relative">
                            <ProfilePicture
                              src={creator.picture}
                              alt={creator.first_name || t('anonymousCreator')}
                              className="h-full w-full"
                              verifiedTitle={t('profilePictureFromGoogle')}
                            />
                          </div>
                          <div className="ml-3">
                            <span className="text-white font-medium italic block">
                              {creator.first_name || t('anonymousCreator')}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-3 text-center">
                          <div className="inline-flex items-center">
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-md font-medium">
                              {uploadCount}
                            </span>
                            {uploadCount > 0 && (
                              <span className="ml-2 text-gray-400 text-sm">
                                {uploadCount > 1 ? t('designLabelPlural') : t('designLabel')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2 text-center">
                          <div className="inline-flex items-center">
                            <span className="bg-cyan-500 text-white px-2 py-1 rounded-md font-medium text-sm">
                              {creator.total_votes || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-[#18181B] py-3 px-6 border-t border-[#232329]">
                  <div className="flex justify-between items-center text-sm text-gray-400">
                    <span>
                      {t('totalVoters')}: {topCreators.length}{' '}
                      {topCreators.length > 1 ? t('creatorsCountPlural') : t('creatorsCount')}
                    </span>
                    <div className="flex space-x-4">
                      <span>
                        {t('footerDesignsTotal')}:{' '}
                        {topCreators.reduce((sum, c) => sum + (c.count || c.upload_count || 0), 0)}
                      </span>
                      <span>
                        {t('footerVotesTotal')}:{' '}
                        {topCreators.reduce((sum, c) => sum + (c.total_votes || 0), 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/"
              className="inline-flex items-center bg-cyan-500 hover:bg-cyan-600 text-black font-bold py-3 px-6 rounded-md transition duration-300 shadow-lg hover:shadow-cyan-500/25"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              {t('backToHome')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TopCreators;
